import { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { apiService } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  Users, 
  Building2, 
  Calendar, 
  Flame,
  TrendingUp,
  Activity,
  Clock,
  MapPin,
  RefreshCw,
  Loader2,
  CheckCircle,
  CalendarCheck,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const CHART_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4'];

export const IgrejaDashboardPage = () => {
  const { igrejaAtual } = useApp();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    if (igrejaAtual?.id) {
      loadDashboard();
    }
  }, [igrejaAtual?.id]);

  const loadDashboard = async () => {
    if (!igrejaAtual?.id) return;
    
    try {
      setLoading(true);
      const data = await apiService.getIgrejaDashboard(igrejaAtual.id);
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
    setRefreshing(false);
    toast.success('Dados atualizados!');
  };

  if (!igrejaAtual) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Nenhuma igreja selecionada</h2>
          <p className="text-muted-foreground">Selecione uma igreja no menu lateral para ver o dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const stats = dashboardData?.estatisticas || {};
  const frequencia = dashboardData?.frequencia_mensal || [];
  const celulas = dashboardData?.celulas || [];

  const statCards = [
    { 
      label: 'Total de Membros', 
      value: stats.total_membros || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      description: 'Membros cadastrados'
    },
    { 
      label: 'Células Ativas', 
      value: stats.total_celulas || 0, 
      icon: Flame, 
      color: 'bg-primary',
      description: 'Grupos em atividade'
    },
    { 
      label: 'Encontros Realizados', 
      value: stats.encontros_realizados || 0, 
      icon: CheckCircle, 
      color: 'bg-green-500',
      description: 'Encontros finalizados'
    },
    { 
      label: 'Encontros Agendados', 
      value: stats.encontros_agendados || 0, 
      icon: CalendarCheck, 
      color: 'bg-purple-500',
      description: 'Próximos encontros'
    },
  ];

  // Prepare pie chart data for células
  const celulasChartData = celulas.map((c, index) => ({
    name: c.nome.length > 15 ? c.nome.substring(0, 15) + '...' : c.nome,
    value: c.total_membros,
    color: CHART_COLORS[index % CHART_COLORS.length]
  })).filter(c => c.value > 0);

  return (
    <div className="page-transition" data-testid="igreja-dashboard-page">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-primary/10 via-purple-500/10 to-blue-500/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary text-primary-foreground">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
                  Dashboard da Igreja
                </h1>
                <p className="text-muted-foreground">
                  {dashboardData?.igreja?.nome || igrejaAtual.nome}
                </p>
              </div>
            </div>
            
            <Button onClick={handleRefresh} variant="outline" disabled={refreshing} data-testid="refresh-dashboard-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
              <Card key={index} className="card-elevated animate-scale-in" style={{ animationDelay: `${index * 100}ms` }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-xl ${stat.color} text-white`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm font-medium text-foreground">{stat.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Frequency Chart */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Frequência Mensal
                </CardTitle>
                <CardDescription>
                  Número de presentes nos encontros por mês
                </CardDescription>
              </CardHeader>
              <CardContent>
                {frequencia.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={frequencia}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="mes" 
                        tick={{ fontSize: 12 }}
                        stroke="#6b7280"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="#6b7280"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [
                          value, 
                          name === 'presentes' ? 'Presentes' : name === 'encontros' ? 'Encontros' : name
                        ]}
                      />
                      <Bar 
                        dataKey="presentes" 
                        fill="#f59e0b" 
                        radius={[4, 4, 0, 0]}
                        name="presentes"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center bg-muted/30 rounded-lg">
                    <div className="text-center">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">Sem dados de frequência</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Os dados aparecerão após encontros finalizados
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Taxa de Presença Chart */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Taxa de Presença
                </CardTitle>
                <CardDescription>
                  Evolução da taxa de presença ao longo do tempo
                </CardDescription>
              </CardHeader>
              <CardContent>
                {frequencia.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={frequencia}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="mes" 
                        tick={{ fontSize: 12 }}
                        stroke="#6b7280"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="#6b7280"
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value}%`, 'Taxa de Presença']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="taxa" 
                        stroke="#10b981" 
                        strokeWidth={3}
                        dot={{ fill: '#10b981', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center bg-muted/30 rounded-lg">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">Sem dados de taxa</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Finalize encontros com presença registrada
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bottom Row */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Distribution Pie Chart */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Membros por Célula
                </CardTitle>
                <CardDescription>
                  Distribuição de membros
                </CardDescription>
              </CardHeader>
              <CardContent>
                {celulasChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={celulasChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {celulasChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value} membros`, '']}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value) => <span className="text-xs">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center bg-muted/30 rounded-lg">
                    <div className="text-center">
                      <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Sem membros nas células</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Células List */}
            <Card className="card-elevated lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-primary" />
                      Células da Igreja
                    </CardTitle>
                    <CardDescription>
                      {celulas.length} células ativas
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/celulas">Ver todas</Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {celulas.length > 0 ? (
                  <div className="space-y-3 max-h-[280px] overflow-y-auto">
                    {celulas.map((celula, index) => (
                      <Link 
                        key={celula.id} 
                        to={`/celulas/${celula.id}`}
                        className="block"
                      >
                        <div 
                          className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors animate-slide-up"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                            />
                            <div>
                              <p className="font-medium text-foreground">{celula.nome}</p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                {celula.dia_semana && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {celula.dia_semana}
                                  </span>
                                )}
                                {celula.horario && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {celula.horario}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-center">
                              <p className="font-semibold text-foreground">{celula.total_membros}</p>
                              <p className="text-xs text-muted-foreground">membros</p>
                            </div>
                            <div className="text-center">
                              <p className="font-semibold text-foreground">{celula.total_encontros}</p>
                              <p className="text-xs text-muted-foreground">encontros</p>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center">
                    <div className="text-center">
                      <Flame className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">Nenhuma célula cadastrada</p>
                      <Button className="mt-4" asChild>
                        <Link to="/celulas">Criar primeira célula</Link>
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
